import React from 'react';
import invert from 'lodash/invert';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

const REGEXP_TO_MODIFY_URL = /\/$/;
const DEFAULT_KEY = 'default';
const KEY_TITLE_MAP = {
  lowercaseKey: 'title',
  capitilizedKey: 'Title',
};
const KEY_DESCRIPTION_MAP = {
  lowercaseKey: 'description',
  capitilizedKey: 'Description',
};

/**
 * Returns updated FAQ Schema from meta config
 * @param { object } metaDataSource
 * @param { string } faqSchema - faqSchema object from metaData
 * @returns {null|Element} Null or script element
 */
const getFaqSchemaMeta = (path, { faqSchema }) => {
  if (faqSchema) {
    return (
      <script data-react-helmet="true" type="application/ld+json" key={`faqSchema-${path}`}>
        {JSON.stringify(faqSchema)}
      </script>
    );
  }
  return null;
};

/**
 * Creates an array of tags for dynamic Title
 * @param { string }  metaTagKey as meta tag property name , like title/description
 * @param { string }  content as content
 * @param { string }  path
 * @returns {*[]}  array of tags
 */
function getTitleTags(metaTagKey, content, path) {
  return [
    <title key={`${metaTagKey}-${path}`}>{content}</title>,
    <meta property={`og:${metaTagKey}`} content={content} key={`meta-property-${metaTagKey}-${path}`} />,
  ];
}

/**
 * Creates a pair of meta tags by given name
 * @param { string }  metaTagKey
 * @param { string }  content
 * @param { string }  path
 * @returns {*[]}
 */
function getMetaTags(metaTagKey, content, path) {
  return [
    <meta name={metaTagKey} content={content} key={`meta-name-${metaTagKey}-${path}`} />,
    <meta property={`og:${metaTagKey}`} content={content} key={`meta-property-${metaTagKey}-${path}`} />,
  ];
}

/**
 * Returns metaData config by specific param if if exits in the config or default value
 * @param { string }  key  original name (Title, Description) - default value
 * @param { string }  subKey, like subTitle
 * @param { string }  pageKey, like signupPlans
 * @returns {*} string
 */
function getDataFromConfigByKey(meta, key, subKey, pageKey) {
  return (meta && meta[subKey] && meta[subKey][pageKey]) || (meta && meta[key]);
}

/**
 * Modifies a path and gets a content from config by particular page or returns a default one
 * @param { object } keyTitleMap
 * @param { string } path, like signupPlans
 * @returns {*}
 */
function getSubContent(pageMeta, { lowercaseKey, capitilizedKey }, path) {
  return getDataFromConfigByKey(pageMeta, lowercaseKey, `sub${capitilizedKey}`, path);
}

/**
 * Modifies a path and gets a content from config by particular page or returns a default one
 * @param { object } pageMeta
 * @param { string } path, like signupPlans
 * @returns {*[]} array with link element
 */
function getCanonicalUrl(url, pathKey) {
  return [<link rel="canonical" href={url} key={`canonical-url-${pathKey}`} />];
}

/**
 * Transforms pathname from locations from '/account/signup-plans/'to signup-plans
 * and returns a new string if a path exists in the object
 * @param { string }  path location pathname
 * @returns {*}
 */
function getPropertyKeyByPath(routes, path) {
  const reversedRoutesMap = invert(routes);
  const modifiedPath = path && path.replace(REGEXP_TO_MODIFY_URL, '');
  return reversedRoutesMap[modifiedPath] || DEFAULT_KEY;
}

/**
 * Renders a Helmet component when any config for the path is exists in a config
 * @param { string } path from location pathname
 * @param { object } routes
 * @param { object } pageMeta
 * @returns {*}
 * @constructor
 */
export function HeaderTitleComponent({ routes, pageMeta, path = '' }) {
  const pathKey = getPropertyKeyByPath(routes, path);
  const subTitleData = getSubContent(pageMeta, KEY_TITLE_MAP, pathKey);
  const subDescriptionData = getSubContent(pageMeta, KEY_DESCRIPTION_MAP, pathKey);
  const canonicalUrlData = pageMeta && pageMeta.og && pageMeta.og.url;
  const { faqSchema } = pageMeta;
  if (subTitleData || subDescriptionData || faqSchema || canonicalUrlData) {
    return (
      <Helmet>
        {subTitleData && getTitleTags(KEY_TITLE_MAP.lowercaseKey, subTitleData, pathKey).map((tag) => tag)}
        {subDescriptionData &&
          getMetaTags(KEY_DESCRIPTION_MAP.lowercaseKey, subDescriptionData, pathKey).map((tag) => tag)}
        {canonicalUrlData && getCanonicalUrl(canonicalUrlData, path)}
        {faqSchema && getFaqSchemaMeta(path, pageMeta)}
      </Helmet>
    );
  }
  return null;
}

export const HeaderTitle = connect(({ appConfig: { routes, pageMeta } }) => ({
  routes,
  pageMeta,
}))(HeaderTitleComponent);
